/** Dependency-free charts (chart.js is not installed). Accessible: every bar has text. Colours by design tokens only. */
import { useMemo } from 'react';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';

export interface Datum { key: string | null; n: number }
const fmtN = (n: number) => n.toLocaleString('es-CO');

/** `color` es una clase de fondo por token (`bg-primary`, `bg-warning`, `bg-success/70`…). */
export function HBarChart({ data, labelOf, empty = 'Sin datos en el periodo.', max = 8, color = 'bg-primary' }: { data: Datum[]; labelOf?: (k: string | null) => string; empty?: string; max?: number; color?: string }) {
  const rows = useMemo(() => [...data].sort((a, b) => b.n - a.n).slice(0, max), [data, max]);
  const top = Math.max(1, ...rows.map((r) => r.n));
  const total = data.reduce((a, b) => a + b.n, 0);
  if (!rows.length) return <div className="text-sm text-muted-foreground">{empty}</div>;
  return (
    <ul className="m-0 list-none p-0 text-sm" aria-label="Distribución">
      {rows.map((r, i) => {
        const etiqueta = labelOf ? labelOf(r.key) : r.key;
        return (
          <li key={`${r.key}-${i}`} className="grid grid-cols-[2fr_3fr_5rem] items-center gap-2 py-1">
            <span className="truncate text-foreground" title={etiqueta ?? 'Sin dato'}>{etiqueta ?? <span className="text-muted-foreground">Sin dato</span>}</span>
            <span className="h-4 overflow-hidden rounded bg-muted"><span className={`block h-full rounded ${color}`} style={{ width: `${(r.n / top) * 100}%`, minWidth: r.n ? 4 : 0 }} /></span>
            <span className="tnum whitespace-nowrap text-right">{fmtN(r.n)} <span className="text-xs text-muted-foreground">{total ? Math.round((r.n / total) * 100) : 0}%</span></span>
          </li>
        );
      })}
    </ul>
  );
}

export function ColumnChart({ data, empty = 'Sin datos en el periodo.' }: { data: Array<{ day: string; n: number }>; empty?: string }) {
  const top = Math.max(1, ...data.map((d) => d.n));
  if (!data.length) return <div className="text-sm text-muted-foreground">{empty}</div>;
  const step = Math.max(1, Math.ceil(data.length / 12));
  return (
    <div>
      <div className="flex items-end gap-[2px] h-40 border-b border-border" role="img" aria-label={`Recibidas por día: ${data.map((d) => `${d.day} ${d.n}`).join(', ')}`}>
        {data.map((d) => (
          <div key={d.day} className="flex-1 h-full flex flex-col justify-end group relative min-w-[3px]" title={`${new Date(d.day + 'T00:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}: ${d.n}`}>
            <div className="bg-primary rounded-t group-hover:bg-primary/80" style={{ height: `${(d.n / top) * 100}%`, minHeight: d.n ? 3 : 0 }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
        {data.filter((_, i) => i % step === 0).map((d) => <span key={d.day}>{new Date(d.day + 'T00:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}</span>)}
      </div>
    </div>
  );
}

export function KpiTile({ label, value, hint, tone = 'neutral', icon }: { label: string; value: string; hint?: string; tone?: 'neutral' | 'ok' | 'warning' | 'critical' | 'info'; icon?: NombreIcono }) {
  const cls = { neutral: 'text-foreground', ok: 'text-success', warning: 'text-warning', critical: 'text-destructive', info: 'text-primary' }[tone];
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-sm">
      <div className="text-xs text-muted-foreground flex items-center gap-1">{icon && <Icono nombre={icon} className="size-3.5" aria-hidden />}{label}</div>
      <div className={`text-2xl font-semibold tnum mt-1 ${cls}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}
